import {
  CustomAuthPublicClientApplication,
  SignInPasswordRequiredState,
  SignInCompletedState,
  SignInFailedState,
  MfaAwaitingState,
  MfaVerificationRequiredState,
  MfaCompletedState,
  AuthMethodRegistrationRequiredState,
  AuthMethodVerificationRequiredState,
  AuthMethodRegistrationCompletedState,
  type CustomAuthConfiguration,
  type ICustomAuthPublicClientApplication,
  type SignInResult,
  type SignInSubmitPasswordResult,
  type SignInError,
  type SignInSubmitPasswordError,
  type MfaRequestChallengeError,
  type MfaSubmitChallengeError,
  type AuthMethodRegistrationChallengeMethodError,
  type AuthMethodRegistrationSubmitChallengeError,
  type AuthenticationMethod,
} from "@azure/msal-browser/custom-auth";
import type { Configuration } from "@azure/msal-browser";

/**
 * Native-authentication config, derived from the `entraExternalId` block of app-web's anonymous
 * /feature-flags payload. Native auth is simply how Entra signs in, so it is active whenever the Entra
 * block is present and enabled - there is no separate native toggle. `clientId`/`authority`/`scopes` are
 * shared with the redirect flow; `challengeTypes` is a fixed default; `authApiProxyUrl` is the one
 * environment-specific value (the dev CORS proxy).
 */
export interface NativeAuthConfig {
  clientId: string;
  authority: string;
  scopes: string[];
  challengeTypes: string[];
  // Optional proxy override for the native-auth API. When omitted the endpoints
  // derive from `authority` (the custom URL domain), which is the direct path.
  authApiProxyUrl?: string;
}

/**
 * A resting point in the native sign-in flow that the UI renders:
 * - `completed` — tokens are in the shared MSAL cache; the caller navigates on.
 * - `failed` — a user-actionable error with a display message; the caller stays
 *   on the current screen so the user can retry.
 * - `fallback` — the server asked to redirect (a capability/challenge this flow
 *   can't satisfy natively); escalate to the hosted redirect flow.
 * - `otp` — an email one-time code is required (MFA challenge or first-time
 *   auth-method registration; both look the same to the user). `submit` advances
 *   the flow and yields the next step.
 */
export type NativeStep =
  | { kind: "completed" }
  | { kind: "failed"; message: string }
  | { kind: "fallback" }
  | {
      kind: "otp";
      sentTo: string;
      codeLength: number;
      submit: (code: string) => Promise<NativeStep>;
      resend: () => Promise<NativeStep>;
    };

interface RedirectableError {
  isRedirectRequired(): boolean;
  errorData?: { errorDescription?: string };
}

const GENERIC_ERROR = "We couldn't sign you in. Please try again.";
const INVALID_CREDENTIALS = "That email or password is incorrect.";
// The native-auth token request uses exactly the scopes we pass (unlike MSAL's
// interactive flows, which add these automatically). `openid` is required for an
// id token to be issued — without it the SDK can't finalize the account.
const OIDC_SCOPES = ["openid", "profile", "offline_access"];
// Challenge types the native UI can satisfy: in-app password, plus a redirect escalation for anything
// it can't handle natively (a capability that needs the hosted page).
const DEFAULT_CHALLENGE_TYPES = ["password", "redirect"];

let nativeClient: ICustomAuthPublicClientApplication | null = null;
let nativeConfig: NativeAuthConfig | null = null;

interface EntraBlock {
  enabled?: boolean;
  authority?: string;
  clientId?: string;
  scopes?: string[];
  authApiProxyUrl?: string;
}

/**
 * Extracts the native-auth config from the /feature-flags payload. Returns null unless the Entra block is
 * present and enabled - native auth is the Entra sign-in experience, so it needs no toggle of its own.
 */
export function readNativeAuthConfig(options: unknown): NativeAuthConfig | null {
  const entra = (options as { entraExternalId?: EntraBlock } | null | undefined)?.entraExternalId;
  if (!entra || entra.enabled !== true || !entra.clientId || !entra.authority || !entra.scopes?.length) {
    return null;
  }

  return {
    clientId: entra.clientId,
    authority: entra.authority,
    scopes: entra.scopes,
    challengeTypes: DEFAULT_CHALLENGE_TYPES,
    authApiProxyUrl: entra.authApiProxyUrl,
  };
}

/** Whether the native-auth flow is active for this session. */
export function isNativeAuthActive(): boolean {
  return nativeClient !== null && nativeConfig !== null;
}

/**
 * Signs the current account out of the native flow: clears its tokens from the
 * shared MSAL cache and redirects to postLogoutRedirectUri. Unlike the hosted
 * redirect logout, it does not hit the CIAM end-session endpoint - native auth
 * never established a browser session there, so that endpoint just shows an empty
 * account picker. Returns false when there is no native client or cached account.
 */
export async function signOutNative(): Promise<boolean> {
  if (!nativeClient) {
    return false;
  }

  const account = nativeClient.getCurrentAccount().data;
  if (!account) {
    return false;
  }

  await account.signOut();
  return true;
}

/**
 * Initializes the native-auth client from the feature-flags config. No-op
 * returning false when native auth is not configured, so the caller keeps the
 * redirect flow. Shares the localStorage MSAL cache with the redirect client
 * (same clientId/authority), so a completed native sign-in resolves through the
 * existing bootstrap after navigation.
 */
export async function initializeNativeAuth(options: unknown): Promise<boolean> {
  const config = readNativeAuthConfig(options);
  if (!config) {
    return false;
  }

  const msalConfig: Configuration = {
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      knownAuthorities: [new URL(config.authority).host],
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: "localStorage",
    },
  };

  const customAuth = {
    challengeTypes: config.challengeTypes,
    // Advertise the capabilities this app can drive natively, so the server runs
    // MFA and first-time auth-method registration in-app instead of falling back
    // to a hosted redirect.
    capabilities: ["mfa_required", "registration_required"],
    ...(config.authApiProxyUrl ? { authApiProxyUrl: config.authApiProxyUrl } : {}),
  } as CustomAuthConfiguration["customAuth"];

  nativeClient = await CustomAuthPublicClientApplication.create({ ...msalConfig, customAuth });
  nativeConfig = config;
  return true;
}

/**
 * Drives a native email + password sign-in and returns the next step for the UI:
 * `completed`, `failed`, `fallback`, or an `otp` step when the tenant requires an
 * email one-time code (MFA challenge or first-time auth-method registration).
 * State transitions are driven by `instanceof` on the SDK's `state` (its boolean
 * predicates are `this is ... & { state }` guards that collapse the type when
 * chained), while `error`/`data` are read from the result base.
 */
export async function startNativeSignIn(username: string, password: string): Promise<NativeStep> {
  if (!nativeClient || !nativeConfig) {
    return { kind: "fallback" };
  }

  const scopes = Array.from(new Set([...OIDC_SCOPES, ...nativeConfig.scopes]));
  const result = await nativeClient.signIn({ username, scopes });
  return advanceSignIn(result, password, username);
}

async function advanceSignIn(result: SignInResult, password: string, username: string): Promise<NativeStep> {
  const state = result.state;

  if (state instanceof SignInCompletedState) {
    return { kind: "completed" };
  }
  if (state instanceof SignInFailedState) {
    return failureOrFallback(result.error, describeSignInError(result.error));
  }
  if (state instanceof SignInPasswordRequiredState) {
    return advancePassword(await state.submitPassword(password), username);
  }
  if (state instanceof MfaAwaitingState) {
    return beginMfa(state);
  }
  if (state instanceof AuthMethodRegistrationRequiredState) {
    return beginRegistration(state, username);
  }
  return { kind: "fallback" };
}

async function advancePassword(result: SignInSubmitPasswordResult, username: string): Promise<NativeStep> {
  const state = result.state;

  if (state instanceof SignInCompletedState) {
    return { kind: "completed" };
  }
  if (state instanceof SignInFailedState) {
    return failureOrFallback(result.error, describePasswordError(result.error));
  }
  if (state instanceof MfaAwaitingState) {
    return beginMfa(state);
  }
  if (state instanceof AuthMethodRegistrationRequiredState) {
    return beginRegistration(state, username);
  }
  return { kind: "fallback" };
}

/** MFA with an already-registered method: request the email challenge, then collect the code. */
async function beginMfa(state: MfaAwaitingState): Promise<NativeStep> {
  const method = pickEmailMethod(state.getAuthMethods());
  if (!method) {
    return { kind: "fallback" };
  }

  const result = await state.requestChallenge(method.id);
  if (result.state instanceof MfaVerificationRequiredState) {
    return mfaOtpStep(result.state, method.id);
  }
  return failureOrFallback(result.error, describeChallengeError(result.error));
}

function mfaOtpStep(state: MfaVerificationRequiredState, methodId: string): NativeStep {
  return {
    kind: "otp",
    sentTo: state.getSentTo(),
    codeLength: state.getCodeLength(),
    submit: async (code: string) => {
      const result = await state.submitChallenge(code);
      if (result.state instanceof MfaCompletedState) {
        return { kind: "completed" };
      }
      return failureOrFallback(result.error, describeOtpError(result.error));
    },
    resend: async () => {
      const result = await state.requestChallenge(methodId);
      if (result.state instanceof MfaVerificationRequiredState) {
        return mfaOtpStep(result.state, methodId);
      }
      return failureOrFallback(result.error, describeChallengeError(result.error));
    },
  };
}

/** First-time setup: register the email method, then collect the verification code. */
async function beginRegistration(state: AuthMethodRegistrationRequiredState, username: string): Promise<NativeStep> {
  const method = pickEmailMethod(state.getAuthMethods());
  if (!method) {
    return { kind: "fallback" };
  }

  const result = await state.challengeAuthMethod({ authMethodType: method, verificationContact: username });
  if (result.state instanceof AuthMethodVerificationRequiredState) {
    return registrationOtpStep(result.state, method, username);
  }
  if (result.state instanceof AuthMethodRegistrationCompletedState) {
    return { kind: "completed" };
  }
  return failureOrFallback(result.error, describeChallengeError(result.error));
}

function registrationOtpStep(
  state: AuthMethodVerificationRequiredState,
  method: AuthenticationMethod,
  username: string,
): NativeStep {
  return {
    kind: "otp",
    sentTo: state.getSentTo(),
    codeLength: state.getCodeLength(),
    submit: async (code: string) => {
      const result = await state.submitChallenge(code);
      if (result.state instanceof AuthMethodRegistrationCompletedState) {
        return { kind: "completed" };
      }
      return failureOrFallback(result.error, describeOtpError(result.error));
    },
    resend: async () => {
      const result = await state.challengeAuthMethod({ authMethodType: method, verificationContact: username });
      if (result.state instanceof AuthMethodVerificationRequiredState) {
        return registrationOtpStep(result.state, method, username);
      }
      if (result.state instanceof AuthMethodRegistrationCompletedState) {
        return { kind: "completed" };
      }
      return failureOrFallback(result.error, describeChallengeError(result.error));
    },
  };
}

function pickEmailMethod(methods: AuthenticationMethod[]): AuthenticationMethod | undefined {
  return methods.find(method => method.challenge_channel === "email") ?? methods[0];
}

function failureOrFallback(error: RedirectableError | undefined, message: string): NativeStep {
  if (error?.isRedirectRequired()) {
    return { kind: "fallback" };
  }
  return { kind: "failed", message };
}

function describeSignInError(error?: SignInError): string {
  if (error?.isUserNotFound() || error?.isInvalidUsername() || error?.isPasswordIncorrect()) {
    return INVALID_CREDENTIALS;
  }
  if (error?.isPasswordResetRequired()) {
    return "Your password must be reset before you can sign in.";
  }
  return error?.errorData?.errorDescription ?? GENERIC_ERROR;
}

function describePasswordError(error?: SignInSubmitPasswordError): string {
  if (error?.isInvalidPassword()) {
    return INVALID_CREDENTIALS;
  }
  return error?.errorData?.errorDescription ?? GENERIC_ERROR;
}

function describeChallengeError(
  error?: MfaRequestChallengeError | AuthMethodRegistrationChallengeMethodError,
): string {
  if (error?.isVerificationContactBlocked()) {
    return "Too many attempts. Please wait a moment and try again.";
  }
  return error?.errorData?.errorDescription ?? GENERIC_ERROR;
}

function describeOtpError(
  error?: MfaSubmitChallengeError | AuthMethodRegistrationSubmitChallengeError,
): string {
  if (error?.isIncorrectChallenge()) {
    return "That code isn't correct. Please try again.";
  }
  return error?.errorData?.errorDescription ?? GENERIC_ERROR;
}
